import { Badge } from '@/components/ui/badge';
import type { Certificado } from '@/api/client';

interface CertificateStatusBadgeProps {
  cert: Certificado;
}

export type CertValidadeTone = 'sem-dados' | 'expirado' | 'expirando' | 'valido';

/** Situação da validade (`validade_fim`, fonte: autenticador) e dias restantes. */
export function certValidade(cert: Certificado): { tone: CertValidadeTone; dias: number } {
  const validade = cert.validade_fim ? new Date(cert.validade_fim) : null;
  if (!validade || Number.isNaN(validade.getTime())) return { tone: 'sem-dados', dias: 0 };
  const msPerDay = 24 * 60 * 60 * 1000;
  const dias = Math.floor((validade.getTime() - Date.now()) / msPerDay);
  if (dias < 0) return { tone: 'expirado', dias };
  if (dias <= 30) return { tone: 'expirando', dias };
  return { tone: 'valido', dias };
}

/**
 * Badge de validade derivado de `validade_fim` (fonte: autenticador).
 *
 * - Verde: "Válido" (> 30 dias restantes)
 * - Amarelo: "Expirando em X dias" (<= 30 dias)
 * - Vermelho: "Expirado"
 */
export function CertificateStatusBadge({ cert }: CertificateStatusBadgeProps) {
  const { tone, dias } = certValidade(cert);

  if (tone === 'sem-dados') {
    return <Badge variant="outline">Sem dados</Badge>;
  }

  if (tone === 'expirado') {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400">
        Expirado
      </Badge>
    );
  }

  if (tone === 'expirando') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400">
        Expirando em {dias} dia{dias === 1 ? '' : 's'}
      </Badge>
    );
  }

  return (
    <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400">
      Válido
    </Badge>
  );
}
